import { CommonModule } from '@angular/common';
import { Component, OnInit, NgZone, OnDestroy } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { AlertController, IonicModule, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { MyEventServices } from './services/my-event.service';
import { LoginService } from './services/login.service';
import { FcmService } from './services/fcm.service';
import { ProfileService } from './services/profile.service';
import { Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Subscription } from 'rxjs';
import { Dialog } from '@capacitor/dialog';
import { ParishConfigService } from './services/parishconfig.service';
import { MegaMenuItem } from 'primeng/api';
import { AdministrativeService } from './services/administrative.service';
import { Menu } from 'src/models/menu';
import { ParishConfig } from 'src/models/parishConfig';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonicModule, RouterLink, RouterLinkActive, CommonModule],
})
export class AppComponent implements OnInit, OnDestroy {
  public appPages: MegaMenuItem[] = [
    { title: 'Home', url: '/home', icon: 'assets/icon/Home.png' },
    { title: 'My Service', url: '/menu', icon: 'assets/icon/Services.png' },
    { title: 'Visitations', url: '/visitations', icon: 'assets/icon/Services.png' },
    { title: 'My Wallet', url: '/wallet', icon: 'assets/icon/Wallet.png' },
    { title: 'Payment History', url: '/paymenthistory', icon: 'assets/icon/Payment.png' },
    { title: 'Login', url: '/signin', icon: 'assets/icon/myaccount.png' },
    { title: 'Feedback', url: '/feedback', icon: 'assets/icon/Feedback.png' },
    { title: 'Settings', url: '/settings', icon: 'assets/icon/Settings.png' },
    { title: 'Change Password', url: '/change-password', icon: 'assets/icon/Password.png' },
    { title: 'Change Parish', url: '/companylist', icon: 'assets/icon/Parish.png' },
    { title: 'Logout', url: '/logout', icon: 'assets/icon/Logout.png' }
  ];

  userMenu: Menu[] = [];
  loggedInMenu = ['Change Password', 'My Service', 'Settings', 'Logout', 'My Wallet', 'Payment History'];
  generalMenu = ['Home', 'Feedback', 'Change Parish', 'Visitations'];

  public labels = [environment.applicationVersion];
  public currentParish: ParishConfig;

  toastMessage = '';
  isToastOpen = false;
  duration = 5000;
  isLoading = false;

  public toastButtons = [
    {
      text: 'Dismiss',
      role: 'cancel'
    }
  ];

  subscriptions: Subscription[] = [];

  constructor(private myEventService: MyEventServices, public userService: LoginService, private profileService: ProfileService,
    private platform: Platform, private alertController: AlertController, private fcmService: FcmService, private parishConfigService: ParishConfigService,
    private router: Router, private authService: AdministrativeService, private zone: NgZone) {
    this.initializeApp();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  ngOnInit(): void {
    this.platform.ready().then(() => {
      this.currentParish = this.parishConfigService.getCurrentParishConfig();

      if (!this.currentParish || !this.currentParish.ApiUrl) {
        this.router.navigate(['/companylist']);
        return;
      }

      this.userService.isAppInReview().subscribe(result => {
        environment.IsAppInReview = result;
      });

      this.userService.logonComplete.subscribe(() => {
        this.fcmService.initPush();

        let userId = 0;

        if (this.userService.currentUser && this.userService.currentUser?.UserId) {
          userId = this.userService.currentUser.UserId;
        }

        this.subscriptions.push(this.userService.getUserMenu(userId, true).subscribe(menu => {
          this.userMenu = menu;
        }));

        this.subscriptions.push(this.userService.saveAppVersion(environment.applicationVersion).subscribe());
      });

      this.myEventService.toastEvent.subscribe(message => {
        let isSticky = message.isSticky ? message.isSticky : false;
        this.duration = isSticky ? 0 : 5000;
        this.toastMessage = message.body;
        this.isToastOpen = true;
      });

      this.myEventService.loadingEvent.subscribe(isShow => {
        this.isLoading = isShow
      });

      if (this.currentParish && this.currentParish.ApiUrl) {
        this.userService.getCurrentUser();

        if (this.userService.currentUser?.UserId > 0) {
          this.userService.logonComplete.emit();
        }

        this.checkVersion();
      }
    });
  }

  initializeApp() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const slug = event.url.split("kononia://").pop();

        this.userService.getCurrentUser();

        if (this.userService.currentUser?.UserId > 0) {

          this.authService.getNewToken().subscribe(() => {
            if (slug && slug.indexOf('takeattendance') > -1) {
              let serviceId = parseInt(slug.split('/')[1]);
              let attendanceDate = new Date(slug.split('/')[2]);

              this.subscriptions.push(this.profileService.saveAttendanceByQR(serviceId, attendanceDate).subscribe(result => {
                // result => -1 = profile not assigned to service
                //            0 = attendance already taken
                //            1 = attendance taken successfully

                if (result && result === 1) {
                  this.myEventService.showToast('success', 'Attendance Saved', 'Your attendance has been taken successfully');
                }
                else if (result && result === -1) {
                  this.myEventService.showToast('error', 'Attendance Failed', 'Make sure you are registered for this service');
                }
                else {
                  this.myEventService.showToast('info', 'Attendance Saved', 'Attendance already taken');
                }
              }));
            }
            else {
              this.router.navigateByUrl(slug);
            }
          });
        }
        else {
          this.myEventService.showToast('error', 'Attendance Failed', 'Please login to take attendance');
          this.router.navigateByUrl('/signin');
        }
      });
    });
  }

  async showProfile() {
    this.profileService.showProfile(this.userService.currentUser?.ProfileId, null, false, false);
  }

  setOpen(isOpen: boolean) {
    this.isToastOpen = isOpen;
  }

  isVersionConfirmed() {
    let versionCheck = sessionStorage.getItem('versioncheck');

    if (!versionCheck) {
      return false;
    }
    else {
      return (versionCheck === 'true');
    }
  }

  setVersionConfirmed() {
    sessionStorage.setItem('versioncheck', 'true');
  }

  isOlderVersion(appVersion, liveVersion) {
    let lstAppVersion = appVersion.split(".");
    let lstLiveVersion = liveVersion.split(".");

    if (parseInt(lstAppVersion[0]) < parseInt(lstLiveVersion[0])) {
      return true;
    }
    else if (parseInt(lstAppVersion[1]) < parseInt(lstLiveVersion[1])) {
      return true;
    }
    else if (parseInt(lstAppVersion[1]) == parseInt(lstLiveVersion[1]) && parseInt(lstAppVersion[2]) < parseInt(lstLiveVersion[2])) {
      return true;
    }
    else {
      return false;
    }
  }

  isMenuItemVisible(menu: any) {
    if (environment.IsAppInReview === true && (menu.url === '/wallet' || menu.url === '/paymenthistory')) {
      return false;
    }

    if (this.generalMenu.includes(menu.title)) {
      return true;
    }

    if (this.userService.currentUser?.UserId && this.loggedInMenu.includes(menu.title)) {
      return true;
    }

    if (!this.userService.currentUser?.UserId && menu.title === 'Login') {
      return true;
    }
    return false;
  }

  checkVersion() {
    let isVersionChecked = this.isVersionConfirmed();

    if (isVersionChecked === false) {
      this.subscriptions.push(this.userService.getApplicationVersion().subscribe(async result => {
        if (result) {
          let currentVersion = environment.applicationVersion;

          this.setVersionConfirmed();

          if (this.isOlderVersion(currentVersion, result.ApplicationVersionNo)) {

            Dialog.confirm({
              title: "New Version",
              message: 'A new version has been released, would you like to update now?',
            }).then(response => {
              if (response.value == true) {
                if (this.platform.is('android')) {
                  window.open("https://play.google.com/store/apps/details?id=com.stmina.kenonia&mode=d", '_system');
                }
                else {
                  window.open("https://apps.apple.com/us/app/st-mina-kononia/id1481729119", '_system');
                }
              }

              if (result.UpdateRequired === true) {
                this.exitApplication();
              }
            });
          }
        }
      }));
    }
  }

  exitApplication() {
    App.exitApp();
  }
}